import {setPromptVariable} from "@khan/guide-core-helpers";

import {
    documentPromptVariable,
    placeholderKeyProperty,
    selectionPromptVariable,
} from "./constants.ts";
import type {
    JsonObject,
    JsonPath,
    JsonValue,
    MaybePlaceholder,
    Placeholder,
} from "./types.ts";

export const getJsonDataAtPath = (root: JsonObject, path: JsonPath) => {
    if (!path.length) {
        return;
    }

    let current: JsonValue = root;

    for (let i = 0; i < path.length; i++) {
        const key = path[i];

        if (current && current.hasOwnProperty(key)) {
            current = current[key];
        } else if (i === path.length - 1 && Array.isArray(current)) {
            // NOTE(reid): A special case. If the last element of the
            // path points to an array index that doesn't exist, we assume
            // the user is trying to insert a new item into the array, so
            // we just return undefined without throwing an error.
            return undefined;
        } else {
            throw new Error("cannot get value at " + path.join("."));
        }
    }

    return current;
};

export const setJsonDataAtPath = (
    root: JsonObject,
    path: JsonPath,
    newValue: JsonValue,
) => {
    if (!path.length) {
        return;
    }

    let current: JsonValue = root;

    for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];

        if (current && current.hasOwnProperty(key)) {
            current = current[key];
        } else {
            throw new Error("cannot set value at " + path.join("."));
        }
    }

    const lastKey = path[path.length - 1];
    if (!current) {
        throw new Error("cannot set value at " + path.join("."));
    }

    current[lastKey] = newValue;
};

export const deleteJsonDataAtPath = (root: JsonObject, path: JsonPath) => {
    if (!path.length) {
        return;
    }

    let current: JsonValue = root;

    for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];

        if (current && current.hasOwnProperty(key)) {
            current = current[key];
        } else {
            throw new Error("cannot delete value at " + path.join("."));
        }
    }

    const lastKey = path[path.length - 1];
    if (!current) {
        throw new Error("cannot delete value at " + path.join("."));
    }

    delete current[lastKey];
};

export const setPlaceholderAtPath = (
    root: JsonObject,
    path: JsonPath,
    placeholderKey: string,
) => {
    setJsonDataAtPath(root, path, {
        [placeholderKeyProperty]: placeholderKey,
    });
};

export const getPlaceholderAtPath = (root: JsonObject, path: JsonPath) => {
    if (!path.length) {
        return;
    }

    const valueAtPath = getJsonDataAtPath(root, path);
    if (!valueAtPath || typeof valueAtPath !== "object") {
        return;
    }

    return valueAtPath[placeholderKeyProperty] || undefined;
};

export const getPlaceholderKey = (rawJson: string): string => {
    const data = JSON.parse(rawJson);
    return data[placeholderKeyProperty] || "";
};

export const isPlaceholder = (
    obj: MaybePlaceholder<any>,
): obj is Placeholder => {
    return obj && typeof obj === "object" && obj[placeholderKeyProperty];
};

export const isNotPlaceholder = <T>(obj: MaybePlaceholder<T>): obj is T => {
    return obj && !isPlaceholder(obj);
};

export const equalPaths = (
    pathA: JsonPath | undefined,
    pathB: JsonPath | undefined,
) => {
    if (!pathA || !pathB) {
        return false;
    }
    if (pathA.length !== pathB.length) {
        return false;
    }
    for (let i = 0; i < pathA.length; i++) {
        if (pathA[i] !== pathB[i]) {
            return false;
        }
    }
    return true;
};

export const withKey = <K extends string, V>(obj: Record<K, V>, key: K) => {
    return {...obj, [key]: true};
};

export const withoutKey = <K extends string, V>(obj: Record<K, V>, key: K) => {
    delete obj[key];
    return {...obj};
};

export const setCoeditingPromptVariables = (
    document: JsonObject,
    selectedPath?: JsonPath,
) => {
    const json = JSON.stringify(document || "{}");
    if (json === "{}") {
        setPromptVariable(documentPromptVariable, null);
        setPromptVariable(selectionPromptVariable, null);
        return;
    }

    setPromptVariable(documentPromptVariable, JSON.stringify(document));

    if (!selectedPath || selectedPath.length === 0) {
        setPromptVariable(selectionPromptVariable, null);
        return;
    }

    try {
        const selection = getJsonDataAtPath(document, selectedPath);
        setPromptVariable(selectionPromptVariable, JSON.stringify(selection));
    } catch {
        // If the document is unmounting, we may fail to get
        // the selection. Clear the selection in this case.
        setPromptVariable(selectionPromptVariable, null);
    }
};

export const isEmptyObject = (obj: JsonObject) => {
    if (obj == null || typeof obj !== "object") {
        return false;
    }
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            return false;
        }
    }
    return true;
};
